import React, { useState, useEffect, useRef } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  BackgroundVariant,
  useReactFlow,
  ReactFlowProvider,
  Position,
} from 'reactflow';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  Divider,
  Button,
  Alert,
  IconButton,
  Tooltip,
  Chip,
  Tabs,
  Tab,
  CircularProgress,
  LinearProgress,
  Drawer,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
  ListItemIcon,
} from '@mui/material';
import 'reactflow/dist/style.css';
import DownloadIcon from '@mui/icons-material/Download';
import {
  CameraAlt,
  Refresh,
  Visibility,
  TrendingUp,
  TrendingDown,
  Close,
} from '@mui/icons-material';
import { AiActions } from '../../Services/_actions/Ai/ai.actions.ts';
import { useRecoilValue } from 'recoil';
import { userInformationAtom } from '../../Services/_states/auth.js';
import { useSnackBar } from '../FlowBuilder/Common/SnackBar/index.tsx';
import { exportFlowToPdf } from '../Shared/FlowToPdf/exportFlowToPdf.tsx';
import { exportPerformanceTableToPdf } from './Exportperformancetabletopdf.tsx';
import TableViewIcon from '@mui/icons-material/TableView';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';

const LEVEL_GAP_X = 500;
const VERTICAL_GAP = 120;

const getPerformanceColor = (score) => {
  if (score === null || score === undefined) return '#ffff';

  // if (score >= 95) return '#5d7ed8ff';
  if (score >= 90) return '#5d7ed8ff';
  // if (score >= 80) return '#059669';
  if (score >= 70) return '#10b981';
  // if (score >= 60) return '#fbbf24';
  if (score >= 50) return '#f59e0b';
  // if (score >= 40) return '#f97316';
  if (score >= 30) return '#d45f5fff';
  // if (score >= 20) return '#dc2626';
  return '#991b1b';
};

const getPerformanceLabel = (score) => {
  if (score === null || score === undefined) return 'Not Tested';

  if (score >= 90) return 'Excellent';
  if (score >= 70) return 'Good';
  if (score >= 50) return 'Moderate';
  if (score >= 30) return 'Below Average';
  return 'Poor';
};

const FlowComponent = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const isCompactView = isMobile || isTablet;

  const { showSnackBar } = useSnackBar();
  const loggedInUser = useRecoilValue(userInformationAtom);
  const userId = loggedInUser?.id || loggedInUser?.user_id || '';

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [allNodes, setAllNodes] = useState([]);
  const [allEdges, setAllEdges] = useState([]);
  const [performanceData, setPerformanceData] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [sidePanelContent, setSidePanelContent] = useState(null);
  const [aiActions] = useState(AiActions());
  const [error, setError] = useState('');
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [showFullView, setShowFullView] = useState(true);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const reactFlow = useReactFlow();
  const [anchorEl, setAnchorEl] = useState(null);

  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const reactFlowContainer = useRef<HTMLDivElement>(null);
  const { fitView } = useReactFlow();
  const fitViewRaf = useRef<number | null>(null);

  const exportAsTablePdf = () => {
    const currentData = performanceData.find((d) => d.subject === selectedSubject);
    exportPerformanceTableToPdf({
      data: currentData,
      fileName: `performance-table-${selectedSubject}-${new Date().toISOString().split('T')[0]}.pdf`,
      onProgress: (msg, type) => showSnackBar(msg, type),
      onSuccess: (msg, type) => showSnackBar(msg, type),
      onError: (msg, type) => showSnackBar(msg, type),
    });
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const scheduleFitView = (options = { padding: 0.3, duration: 400 }) => {
    if (fitViewRaf.current !== null) {
      cancelAnimationFrame(fitViewRaf.current);
    }
    fitViewRaf.current = requestAnimationFrame(() => {
      setTimeout(() => fitView(options), 0);
    });
  };

  useEffect(() => {
    return () => {
      if (fitViewRaf.current !== null) {
        cancelAnimationFrame(fitViewRaf.current);
      }
    };
  }, []);

  useEffect(() => {
    if (userId) {
      fetchPerformanceData();
    }
  }, [userId]);

  const fetchPerformanceData = async () => {
    try {
      setLoading(true);
      const response = await aiActions.getStudentPerformanceWithKnowledgeMap(userId);
      const data = response.data || [];
      setPerformanceData(data);
      const subjects = [...new Set(data.map((d) => d.subject))].sort();
      setAvailableSubjects(subjects);
      if (subjects.length > 0) {
        setSelectedSubject(subjects[0]);
        setTabValue(0);
      }
      setLoading(false);
    } catch (err) {
      console.error(err);
      setError('Failed to load performance data');
      showSnackBar('Failed to load performance data', 'error');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedSubject && performanceData.length > 0) {
      buildFlow();
    } else {
      setNodes([]);
      setEdges([]);
      setAllNodes([]);
      setAllEdges([]);
      setSidePanelContent(null);
    }
  }, [selectedSubject, performanceData]);

  const flattenPrerequisites = (prereqObj, parentId = null, level = 0) => {
    const result = [];

    if (prereqObj && typeof prereqObj === 'object' && prereqObj.name) {
      result.push({
        id: prereqObj.id,
        name: prereqObj.name,
        parentId: parentId,
        level: level,
        hasChildren: prereqObj.children && prereqObj.children.length > 0,
      });

      if (prereqObj.children && prereqObj.children.length > 0) {
        prereqObj.children.forEach((child) => {
          result.push(...flattenPrerequisites(child, prereqObj.id, level + 1));
        });
      }
    }

    return result;
  };

  const estimateHeight = (label) => {
    const base = 40;
    const perLine = 18;
    const charsPerLine = 20;
    const lines = Math.ceil(label.length / charsPerLine);
    return base + (lines - 1) * perLine;
  };

  const relayoutFilteredNodes = (nodesToLayout, clickedNode) => {
    const layoutNodes = [...nodesToLayout];
    const subtopicNode = layoutNodes.find((n) => n.id === clickedNode.id);

    if (!subtopicNode) return layoutNodes;

    const startX = isMobile ? 50 : 100;
    const startY = 300;
    subtopicNode.position = { x: startX, y: startY };

    const prereqsByLevel = {};
    const parentChildMap = {};

    layoutNodes.forEach((n) => {
      if (n.data.type === 'prerequisite') {
        const level = n.data.level || 0;
        if (!prereqsByLevel[level]) prereqsByLevel[level] = [];
        prereqsByLevel[level].push(n);

        const parentId = n.data.prerequisiteData?.parentId;
        if (parentId) {
          const parentNodeId = `prereq_${parentId}`;
          if (!parentChildMap[parentNodeId]) {
            parentChildMap[parentNodeId] = [];
          }
          parentChildMap[parentNodeId].push(n);
        }
      }
    });

    if (prereqsByLevel[0]) {
      const level0Prereqs = prereqsByLevel[0];
      const xOffset = startX + (isMobile ? 250 : 400);
      const verticalSpacing = isMobile ? 100 : 150;
      const totalHeight = (level0Prereqs.length - 1) * verticalSpacing;
      const startYForLevel = startY - totalHeight / 2;

      level0Prereqs.forEach((prereq, idx) => {
        prereq.position = {
          x: xOffset,
          y: startYForLevel + idx * verticalSpacing,
        };
      });
    }

    Object.keys(prereqsByLevel).forEach((levelKey) => {
      const level = parseInt(levelKey);
      if (level === 0) return;

      const prereqs = prereqsByLevel[level];
      const xOffset = startX + (isMobile ? 250 : 400) + level * (isMobile ? 300 : 450);

      const positioned = new Set();

      prereqs.forEach((prereq) => {
        if (positioned.has(prereq.id)) return;

        const parentId = prereq.data.prerequisiteData?.parentId;
        const parentNodeId = `prereq_${parentId}`;
        const parentNode = layoutNodes.find((n) => n.id === parentNodeId);

        if (parentNode && parentNode.position) {
          const siblings = parentChildMap[parentNodeId] || [prereq];
          const siblingIndex = siblings.findIndex((s) => s.id === prereq.id);

          const childSpacing = isMobile ? 80 : 120;
          const totalHeight = (siblings.length - 1) * childSpacing;
          const startYForSiblings = parentNode.position.y - totalHeight / 2;

          prereq.position = {
            x: xOffset,
            y: startYForSiblings + siblingIndex * childSpacing,
          };

          positioned.add(prereq.id);
        } else {
          const unpositioned = prereqs.filter((p) => !positioned.has(p.id));
          const idx = unpositioned.findIndex((p) => p.id === prereq.id);
          prereq.position = {
            x: xOffset,
            y: startY + idx * (isMobile ? 80 : 120),
          };
          positioned.add(prereq.id);
        }
      });
    });

    return layoutNodes;
  };

  const relayoutAllNodes = (nodesToLayout) => {
    const layoutNodes = [...nodesToLayout];

    const subtopics = layoutNodes.filter((n) => n.data.type === 'subtopic');
    const prerequisites = layoutNodes.filter((n) => n.data.type === 'prerequisite');

    prerequisites.sort((a, b) => {
      const levelA = a.data.level || 0;
      const levelB = b.data.level || 0;
      return levelA - levelB;
    });

    const prereqsByLevel = {};
    prerequisites.forEach((node) => {
      const level = node.data.level || 0;
      if (!prereqsByLevel[level]) prereqsByLevel[level] = [];
      prereqsByLevel[level].push(node);
    });

    const subtopicStartX = isMobile ? 50 : 100;
    const subtopicStartY = 100;
    const subtopicSpacing = isMobile ? 100 : 150;

    subtopics.forEach((node, idx) => {
      node.position = {
        x: subtopicStartX,
        y: subtopicStartY + idx * subtopicSpacing,
      };
    });

    const subtopicYMap = {};
    subtopics.forEach((node) => {
      subtopicYMap[node.id] = node.position.y;
    });

    Object.keys(prereqsByLevel).forEach((levelKey) => {
      const level = parseInt(levelKey);
      const prereqs = prereqsByLevel[level];
      const xOffset = subtopicStartX + (isMobile ? 300 : 500) + level * (isMobile ? 300 : 450);

      if (level === 0) {
        prereqs.forEach((prereq) => {
          const connectedSubtopicIds = new Set();
          allEdges.forEach((edge) => {
            if (edge.target === prereq.id && edge.source.startsWith('subtopic_')) {
              connectedSubtopicIds.add(edge.source);
            }
          });

          let avgY = subtopicStartY;
          if (connectedSubtopicIds.size > 0) {
            const yPositions = Array.from(connectedSubtopicIds)
              .map((id) => subtopicYMap[id])
              .filter((y) => y !== undefined);

            if (yPositions.length > 0) {
              avgY = yPositions.reduce((sum, y) => sum + y, 0) / yPositions.length;
            }
          }

          prereq.position = {
            x: xOffset,
            y: avgY,
          };
        });
      } else {
        const parentChildMap = {};
        prereqs.forEach((prereq) => {
          const parentId = prereq.data.prerequisiteData?.parentId;
          if (parentId) {
            const parentNodeId = `prereq_${parentId}`;
            if (!parentChildMap[parentNodeId]) {
              parentChildMap[parentNodeId] = [];
            }
            parentChildMap[parentNodeId].push(prereq);
          }
        });

        Object.entries(parentChildMap).forEach(([parentNodeId, children]) => {
          const parentNode = layoutNodes.find((n) => n.id === parentNodeId);
          if (!parentNode) return;

          const parentY = parentNode.position.y;
          const childSpacing = isMobile ? 80 : 120;
          const totalHeight = (children.length - 1) * childSpacing;
          const startY = parentY - totalHeight / 2;

          children.forEach((child, idx) => {
            child.position = {
              x: xOffset,
              y: startY + idx * childSpacing,
            };
          });
        });

        prereqs.forEach((prereq, idx) => {
          if (!prereq.position) {
            prereq.position = {
              x: xOffset,
              y: subtopicStartY + idx * (isMobile ? 80 : 120),
            };
          }
        });
      }
    });

    return layoutNodes;
  };

  const buildFlow = () => {
    const currentData = performanceData.find((d) => d.subject === selectedSubject);

    if (!currentData || !currentData.topics) {
      setNodes([]);
      setEdges([]);
      setAllNodes([]);
      setAllEdges([]);
      return;
    }

    const newNodes = [];
    const newEdges = [];
    const topics = currentData.topics;

    const subtopicData = {};
    topics.forEach((topic) => {
      topic.subtopics?.forEach((st) => {
        subtopicData[st.subtopic] = st;
      });
    });

    const totalTopics = topics.length;
    const totalHeight = (totalTopics - 1) * VERTICAL_GAP;
    const startY = Math.max(50, 400 - totalHeight / 2);

    const subtopicList = Object.keys(subtopicData).sort();

    let currentY = startY;
    const subtopicYPositions = {};

    subtopicList.forEach((subtopicName) => {
      const label = subtopicName;
      const heightNeeded = estimateHeight(label);
      subtopicYPositions[subtopicName] = currentY;
      currentY += heightNeeded + (isMobile ? 70 : 100);
    });

    subtopicList.forEach((subtopicName) => {
      const subtopic = subtopicData[subtopicName];
      const score = subtopic.best_score;
      const color = getPerformanceColor(score);
      const nodeId = `subtopic_${subtopicName.replace(/\s+/g, '_')}`;

      let parentTopicName = '';
      for (const topic of topics) {
        if (topic.subtopics?.some((st) => st.subtopic === subtopicName)) {
          parentTopicName = topic.topic;
          break;
        }
      }

      newNodes.push({
        id: nodeId,
        data: {
          label: (
            <Tooltip
              title={
                <Box sx={{ p: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 700, display: 'block', mb: 1, color: 'black' }}
                  >
                    {subtopicName}
                  </Typography>
                  <Divider sx={{ my: 1, bgcolor: 'rgba(170, 160, 160, 0.3)' }} />
                  <Box sx={{ mb: 0.5 }}>
                    <Typography variant="caption" sx={{ display: 'block', color: 'black' }}>
                      Learning Status: <strong>{subtopic.learning_status || 'Not Started'}</strong>
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 0.5 }}>
                    <Typography variant="caption" sx={{ display: 'block', color: 'black' }}>
                      Tests Taken: <strong>{subtopic.tests_taken || 0}</strong>
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 0.5 }}>
                    <Typography variant="caption" sx={{ display: 'block', color: 'black' }}>
                      Best Score:{' '}
                      <strong>
                        {subtopic.best_score !== null ? `${subtopic.best_score}%` : 'N/A'}
                      </strong>
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 0.5 }}>
                    <Typography variant="caption" sx={{ display: 'block', color: 'black' }}>
                      Worst Score:{' '}
                      <strong>
                        {subtopic.worst_score !== null ? `${subtopic.worst_score}%` : 'N/A'}
                      </strong>
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 0.5 }}>
                    <Typography variant="caption" sx={{ display: 'block', color: 'black' }}>
                      Trend: <strong>{subtopic.trend || 'N/A'}</strong>
                    </Typography>
                  </Box>
                  {subtopic.last_tested && (
                    <Box sx={{ mb: 0.5 }}>
                      <Typography variant="caption" sx={{ display: 'block', color: 'black' }}>
                        Last Tested: <strong>{subtopic.last_tested}</strong>
                      </Typography>
                    </Box>
                  )}
                  <Divider sx={{ my: 1, bgcolor: 'rgba(170, 160, 160, 0.3)' }} />
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', mb: 0.5, color: 'black' }}
                    >
                      Progress: {subtopic.learned_percentage || 0}%
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={subtopic.learned_percentage || 0}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        bgcolor: 'rgba(0,0,0,0.1)',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: 'rgba(12, 206, 240, 0.9)',
                          borderRadius: 3,
                        },
                      }}
                    />
                  </Box>
                </Box>
              }
              arrow
              placement="right"
              componentsProps={{
                tooltip: {
                  sx: {
                    // bgcolor: color,
                    bgcolor: '#ffffff',
                    color: '#ffffff',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    maxWidth: 280,
                    border: `1px solid rgba(255,255,255,0.2)`,
                  },
                },
                arrow: {
                  sx: {
                    color: color,
                  },
                },
              }}
            >
              <div>
                <div style={{ marginBottom: '4px', fontSize: isMobile ? '11px' : '13px' }}>
                  {subtopicName}
                  {parentTopicName ? ` (${parentTopicName})` : ''}
                </div>
                <LinearProgress
                  variant="determinate"
                  value={subtopic.learned_percentage || 0}
                  sx={{
                    height: 4,
                    borderRadius: 2,
                    bgcolor: 'rgba(0,0,0,0.1)',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: 'rgba(255,255,255,0.9)',
                      borderRadius: 2,
                    },
                  }}
                />
              </div>
            </Tooltip>
          ),
          type: 'subtopic',
          fullData: subtopic,
          score: score,
          performanceLabel: getPerformanceLabel(score),
        },
        position: { x: isMobile ? 50 : 100, y: subtopicYPositions[subtopicName] },
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
        style: {
          background: color,
          border: `2px solid ${color}`,
          borderRadius: isMobile ? '8px' : '12px',
          padding: isMobile ? '8px 12px' : '12px 20px',
          fontSize: isMobile ? '11px' : '13px',
          fontWeight: '600',
          color: '#000000',
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          minWidth: isMobile ? '120px' : '160px',
          maxWidth: isMobile ? '160px' : '220px',
          textAlign: 'center',
          transition: 'all 0.3s ease',
        },
      });
    });

    const allPrerequisitesMap = new Map();
    const prerequisiteRelationships = new Map();

    Object.entries(subtopicData).forEach(([subtopicName, data]) => {
      if (data.prerequisites && Array.isArray(data.prerequisites)) {
        data.prerequisites.forEach((prereq) => {
          const flattened = flattenPrerequisites(prereq);
          flattened.forEach((flatPrereq) => {
            if (!allPrerequisitesMap.has(flatPrereq.id)) {
              allPrerequisitesMap.set(flatPrereq.id, {
                ...flatPrereq,
                connectedSubtopics: new Set(),
              });
            }
            if (flatPrereq.level === 0) {
              allPrerequisitesMap.get(flatPrereq.id).connectedSubtopics.add(subtopicName);
            }

            if (flatPrereq.parentId) {
              if (!prerequisiteRelationships.has(flatPrereq.parentId)) {
                prerequisiteRelationships.set(flatPrereq.parentId, []);
              }
              prerequisiteRelationships.get(flatPrereq.parentId).push(flatPrereq.id);
            }
          });
        });
      }
    });

    const prerequisitePositions = new Map();

    const sortedPrereqs = Array.from(allPrerequisitesMap.entries()).sort((a, b) => {
      if (a[1].level !== b[1].level) return a[1].level - b[1].level;

      const aConnected = Array.from(a[1].connectedSubtopics);
      const bConnected = Array.from(b[1].connectedSubtopics);

      if (aConnected.length > 0 && bConnected.length > 0) {
        const aAvgY =
          aConnected.reduce((sum, name) => sum + subtopicYPositions[name], 0) / aConnected.length;
        const bAvgY =
          bConnected.reduce((sum, name) => sum + subtopicYPositions[name], 0) / bConnected.length;
        return aAvgY - bAvgY;
      }

      return 0;
    });

    const prereqsByLevel = {};
    sortedPrereqs.forEach(([prereqId, prereqData]) => {
      if (!prereqsByLevel[prereqData.level]) prereqsByLevel[prereqData.level] = [];
      prereqsByLevel[prereqData.level].push([prereqId, prereqData]);
    });

    Object.keys(prereqsByLevel).forEach((levelKey) => {
      const level = parseInt(levelKey);
      const prereqsAtLevel = prereqsByLevel[level];
      const baseX = (isMobile ? 50 : 100) + (isMobile ? 300 : 500);
      const xPos = baseX + level * (isMobile ? 300 : 450);

      if (level === 0) {
        prereqsAtLevel.forEach(([prereqId, prereqData]) => {
          const connectedSubtopics = Array.from(prereqData.connectedSubtopics);
          let yPos = startY;

          if (connectedSubtopics.length > 0) {
            const avgY =
              connectedSubtopics.reduce((sum, name) => sum + subtopicYPositions[name], 0) /
              connectedSubtopics.length;
            yPos = avgY;
          }

          prerequisitePositions.set(prereqId, { x: xPos, y: yPos });
        });
      } else {
        prereqsAtLevel.forEach(([prereqId, prereqData]) => {
          const parentId = prereqData.parentId;
          const parentPos = prerequisitePositions.get(parentId);

          if (parentPos) {
            const siblings = prereqsAtLevel.filter(([id, data]) => data.parentId === parentId);
            const siblingIndex = siblings.findIndex(([id]) => id === prereqId);

            const totalSiblings = siblings.length;
            const spacing = isMobile ? 70 : 100;
            const totalHeight = (totalSiblings - 1) * spacing;
            const startYForSiblings = parentPos.y - totalHeight / 2;
            const yPos = startYForSiblings + siblingIndex * spacing;

            prerequisitePositions.set(prereqId, { x: xPos, y: yPos });
          } else {
            prerequisitePositions.set(prereqId, { x: xPos, y: startY });
          }
        });
      }
    });

    sortedPrereqs.forEach(([prereqId, prereqData]) => {
      const position = prerequisitePositions.get(prereqId);
      if (!position) return;

      const xPos = position.x;
      const yPos = position.y;

      let prereqColor = '#ffff';
      const connectedSubtopics = Array.from(prereqData.connectedSubtopics);
      if (connectedSubtopics.length > 0) {
        const connectedScore = subtopicData[connectedSubtopics[0]]?.best_score;
        const baseColor = getPerformanceColor(connectedScore);
        prereqColor = baseColor === '#94a3b8' ? '#e2e8f0' : `${baseColor}`;
      }

      newNodes.push({
        id: `prereq_${prereqId}`,
        data: {
          label: prereqData.name,
          type: 'prerequisite',
          level: prereqData.level,
          prerequisiteData: prereqData,
        },
        position: { x: xPos, y: yPos },
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
        style: {
          background: prereqColor,
          border: `2px solid ${prereqColor === '#e2e8f0' ? '#cbd5e1' : prereqColor}`,
          borderRadius: isMobile ? '6px' : '10px',
          padding:
            prereqData.level === 0
              ? isMobile
                ? '6px 10px'
                : '10px 16px'
              : isMobile
                ? '5px 8px'
                : '8px 14px',
          fontSize:
            prereqData.level === 0 ? (isMobile ? '10px' : '12px') : isMobile ? '9px' : '11px',
          fontWeight: '600',
          color: '#000000',
          cursor: 'pointer',
          boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
          minWidth:
            prereqData.level === 0 ? (isMobile ? '100px' : '140px') : isMobile ? '90px' : '120px',
          maxWidth: isMobile ? '150px' : '200px',
          textAlign: 'center',
          transition: 'all 0.3s ease',
        },
      });

      if (prereqData.level === 0) {
        connectedSubtopics.forEach((subtopicName) => {
          const subtopicId = `subtopic_${subtopicName.replace(/\s+/g, '_')}`;
          const connectedScore = subtopicData[subtopicName]?.best_score;
          const edgeColor = getPerformanceColor(connectedScore);

          newEdges.push({
            id: `${subtopicId}-prereq_${prereqId}`,
            source: subtopicId,
            target: `prereq_${prereqId}`,
            style: {
              stroke: sidePanelContent ? edgeColor : '#000000',
              strokeWidth: isMobile ? 1.5 : 2,
              strokeDasharray: '5,5',
            },
            type: 'smoothstep',
            animated: false,
          });
        });
      }
    });

    prerequisiteRelationships.forEach((childIds, parentId) => {
      childIds.forEach((childId) => {
        const parentData = allPrerequisitesMap.get(parentId);
        let edgeColor = '#cbd5e1';

        if (parentData && parentData.connectedSubtopics.size > 0) {
          const firstSubtopic = Array.from(parentData.connectedSubtopics)[0];
          const connectedScore = subtopicData[firstSubtopic]?.best_score;
          edgeColor = getPerformanceColor(connectedScore);
        }

        newEdges.push({
          id: `prereq_${parentId}-prereq_${childId}`,
          source: `prereq_${parentId}`,
          target: `prereq_${childId}`,
          style: {
            stroke: sidePanelContent ? edgeColor : '#000000',
            strokeWidth: isMobile ? 1.5 : 2,
            strokeDasharray: '5,5',
          },
          type: 'smoothstep',
          animated: false,
        });
      });
    });

    setAllNodes(newNodes);
    setAllEdges(newEdges);
    setNodes(newNodes);
    setEdges(newEdges);
    setShowFullView(true);
    setSelectedNode(null);

    scheduleFitView({ padding: 0.3, duration: 400 });
  };

  const handleNodeClick = (event, node) => {
    const currentData = performanceData.find((d) => d.subject === selectedSubject);

    if (!currentData) return;

    if (isCompactView) {
      setDrawerOpen(true);
    }

    if (node.data.type === 'subtopic') {
      setSelectedNode(node.id);
      setShowFullView(false);

      const subtopicName = node.id.replace('subtopic_', '').replace(/_/g, ' ');
      const subtopicDataMap = {};
      currentData.topics.forEach((topic) => {
        topic.subtopics?.forEach((st) => {
          subtopicDataMap[st.subtopic] = st;
        });
      });

      const currentSubtopicData = subtopicDataMap[subtopicName];
      const allPrerequisiteIds = new Set();

      if (currentSubtopicData?.prerequisites) {
        const collectPrerequisiteIds = (prereqList) => {
          prereqList.forEach((prereq) => {
            if (typeof prereq === 'object' && prereq.name) {
              const flattened = flattenPrerequisites(prereq);
              flattened.forEach((flatPrereq) => {
                allPrerequisiteIds.add(flatPrereq.id);
              });
            }
          });
        };

        collectPrerequisiteIds(currentSubtopicData.prerequisites);
      }

      allEdges.forEach((edge) => {
        if (edge.source === node.id && edge.target.startsWith('prereq_')) {
          const prereqId = edge.target.replace('prereq_', '');
          allPrerequisiteIds.add(prereqId);
        }
      });

      const filteredNodes = allNodes.filter((n) => {
        if (n.id === node.id) return true;

        if (n.data.type === 'prerequisite') {
          const prereqId = n.id.replace('prereq_', '');
          return allPrerequisiteIds.has(prereqId);
        }

        return false;
      });

      const visibleNodeIds = new Set(filteredNodes.map((n) => n.id));
      const filteredEdges = allEdges.filter(
        (edge) => visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target)
      );

      const relayoutedNodes = relayoutFilteredNodes(filteredNodes, node);

      setNodes(relayoutedNodes);
      setEdges(filteredEdges);

      const prereqNames = [];
      const allPrereqDetails = [];

      if (currentSubtopicData?.prerequisites) {
        currentSubtopicData.prerequisites.forEach((prereq) => {
          if (typeof prereq === 'object' && prereq.name) {
            const flattened = flattenPrerequisites(prereq);
            prereqNames.push(...flattened.map((p) => `${'  '.repeat(p.level)}${p.name}`));
            allPrereqDetails.push(...flattened);
          } else if (typeof prereq === 'string') {
            prereqNames.push(prereq);
          }
        });
      }

      setSidePanelContent({
        type: 'subtopic',
        subtopic: node.data.label,
        prerequisites: prereqNames,
        allPrerequisites: allPrereqDetails,
        subtopicData: currentSubtopicData,
        score: node.data.score,
        performanceLabel: node.data.performanceLabel,
      });

      scheduleFitView({ padding: 0.5, duration: 400 });
    } else if (node.data.type === 'prerequisite') {
      setSidePanelContent({
        type: 'prerequisite',
        prerequisite: node.data.label,
        level: node.data.level || 0,
        prerequisiteData: node.data.prerequisiteData,
      });
    }
  };

  const handleViewComplete = () => {
    const relayoutedAllNodes = relayoutAllNodes(allNodes);
    setNodes(relayoutedAllNodes);
    setEdges(allEdges);
    setShowFullView(true);
    setSelectedNode(null);
    setSidePanelContent(null);
    if (isCompactView) {
      setDrawerOpen(false);
    }
    scheduleFitView({ padding: 0.3, duration: 400 });
  };

  const handleReset = () => {
    if (selectedSubject) {
      buildFlow();
    }
  };

  const injectEdgesImage = async (container: HTMLElement) => {
    const viewport = container.querySelector('.react-flow__viewport');
    const edges = viewport?.querySelector('.react-flow__edges');
    const svg = edges?.querySelector('svg');
    const g = svg?.querySelector('g');

    if (!svg || !g || !edges) return null;

    const viewBox = svg.viewBox.baseVal;
    const width = viewBox.width;
    const height = viewBox.height;

    // Read transform matrix: translate(x,y) scale(z)
    const transform = g.getAttribute('transform') || '';

    const serializer = new XMLSerializer();
    let svgStr = serializer.serializeToString(svg);

    // 🔥 Force correct size and keep transform
    svgStr = svgStr.replace(
      '<svg',
      `<svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}"`
    );

    const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    const img = new Image();
    img.src = url;
    await new Promise((r) => (img.onload = r));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(img, 0, 0);

    URL.revokeObjectURL(url);

    const overlay = document.createElement('img');
    overlay.src = canvas.toDataURL();
    overlay.style.position = 'absolute';
    overlay.style.left = '0';
    overlay.style.top = '0';
    overlay.style.width = width + 'px';
    overlay.style.height = height + 'px';
    overlay.style.pointerEvents = 'none';
    overlay.style.zIndex = '10';

    viewport.appendChild(overlay);
    return overlay;
  };

  const getFlowBounds = (nodes: any[]) => {
    let minX = Infinity,
      minY = Infinity;
    let maxX = -Infinity,
      maxY = -Infinity;

    nodes.forEach((n) => {
      const w = n.width || 200;
      const h = n.height || 80;

      minX = Math.min(minX, n.position.x);
      minY = Math.min(minY, n.position.y);
      maxX = Math.max(maxX, n.position.x + w);
      maxY = Math.max(maxY, n.position.y + h);
    });

    return {
      minX,
      minY,
      width: maxX - minX,
      height: maxY - minY,
    };
  };

  const exportToPdf = async () => {
    setExporting(true);

    await exportFlowToPdf({
      nodes,
      reactFlowWrapper: reactFlowContainer,
      fileName: `performance-map-${selectedSubject}-${new Date().toISOString().split('T')[0]}.pdf`,
      backgroundColor: '#f3f5e3ff',
      rowsPerPage: 4,
      rowGap: 150,
      padding: 100,
      minHeight: 400,
      pixelRatio: 2.5,
      onProgress: (message, type) => showSnackBar(message, type),
      onError: (message, type) => {
        showSnackBar(message, type);
        setExporting(false);
      },
      onSuccess: (message, type) => {
        showSnackBar(message, type);
        setExporting(false);
      },
    });
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setSelectedSubject(availableSubjects[newValue]);
  };

  const SidePanelContent = () => (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {nodes.length > 0 && (
          <>
            {!showFullView && (
              <Tooltip title="View Complete Map">
                <Button
                  onClick={handleViewComplete}
                  variant="contained"
                  size="small"
                  startIcon={<Visibility />}
                  sx={{
                    flexGrow: 1,
                    bgcolor: '#667eea',
                    '&:hover': { bgcolor: '#5568d3' },
                    textTransform: 'none',
                    fontSize: isMobile ? '12px' : '14px',
                  }}
                >
                  View Complete
                </Button>
              </Tooltip>
            )}
            <Tooltip title="Reset View">
              <IconButton onClick={handleViewComplete} sx={{ bgcolor: '#f1f5f9' }} size="small">
                <Refresh fontSize={isMobile ? 'small' : 'medium'} />
              </IconButton>
            </Tooltip>
            <Tooltip title={exporting ? 'Exporting...' : 'Export Performance Map'}>
              <IconButton
                onClick={exportToPdf}
                sx={{ bgcolor: '#f1f5f9' }}
                size="small"
                disabled={exporting}
              >
                {exporting ? (
                  <CircularProgress size={20} />
                ) : (
                  <CameraAlt fontSize={isMobile ? 'small' : 'medium'} />
                )}
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>

      <Paper
        elevation={0}
        sx={{ mb: 2, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fafafa' }}
      >
        <Box sx={{ p: isMobile ? 1.5 : 2 }}>
          <Typography
            variant="subtitle2"
            fontWeight={700}
            sx={{ mb: 2, color: '#1e293b', fontSize: isMobile ? '13px' : '14px' }}
          >
            📈 Performance Scale
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {[
              { color: '#5d7ed8ff', range: '90-100', text: 'Excellent' },
              // { color: '#3b82f6', range: '90-94', text: 'Excellent' },
              { color: '#10b981', range: '70-89', text: 'Good' },
              { color: '#f59e0b', range: '50-69', text: 'Moderate' },
              { color: '#d45f5fff', range: '30-49', text: 'Below Avg' },
              { color: '#991b1b', range: '0-29', text: 'Poor' },
              { color: '#94a3b8', range: '', text: 'Not Tested' },
            ].map(({ color, range, text }) => (
              <Box key={range} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    minWidth: isMobile ? 20 : 24,
                    height: isMobile ? 20 : 24,
                    bgcolor: color,
                    borderRadius: 1,
                    border: '1px solid rgba(0,0,0,0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: '#ffffff', fontSize: isMobile ? '8px' : '9px', fontWeight: 700 }}
                  >
                    {range.split('%')[0]}%
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 500, color: '#475569', fontSize: isMobile ? '11px' : '12px' }}
                >
                  {text}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Paper>

      {sidePanelContent && (
        <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
          <Box sx={{ p: isMobile ? 2 : 3 }}>
            {sidePanelContent.type === 'subtopic' && (
              <>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: '#1e293b',
                    mb: 2,
                    fontSize: isMobile ? '16px' : '18px',
                  }}
                >
                  📚 {sidePanelContent.subtopic}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                  <Chip
                    label={`Score: ${sidePanelContent.score !== null ? `${sidePanelContent.score}%` : 'N/A'}`}
                    color={
                      sidePanelContent.score >= 70
                        ? 'success'
                        : sidePanelContent.score >= 50
                          ? 'warning'
                          : 'error'
                    }
                    size="small"
                    sx={{ fontWeight: 600, fontSize: isMobile ? '11px' : '12px' }}
                  />
                  <Chip
                    label={sidePanelContent.performanceLabel}
                    size="small"
                    sx={{
                      bgcolor: getPerformanceColor(sidePanelContent.score),
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: isMobile ? '11px' : '12px',
                    }}
                  />
                </Box>

                {sidePanelContent.subtopicData && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 700,
                        color: '#1e293b',
                        mb: 2,
                        fontSize: isMobile ? '13px' : '14px',
                      }}
                    >
                      📊 Performance Statistics
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          p: isMobile ? 1 : 1.5,
                          bgcolor: '#f8fafc',
                          borderRadius: 2,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: '#64748b', fontSize: isMobile ? '12px' : '14px' }}
                        >
                          Tests Taken
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            color: '#1e293b',
                            fontSize: isMobile ? '12px' : '14px',
                          }}
                        >
                          {sidePanelContent.subtopicData.tests_taken || 0}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          p: isMobile ? 1 : 1.5,
                          bgcolor: '#f0fdf4',
                          borderRadius: 2,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: '#64748b', fontSize: isMobile ? '12px' : '14px' }}
                        >
                          Best Score
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            color: '#10b981',
                            fontSize: isMobile ? '12px' : '14px',
                          }}
                        >
                          {sidePanelContent.subtopicData.best_score !== null
                            ? `${sidePanelContent.subtopicData.best_score}%`
                            : 'N/A'}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          p: isMobile ? 1 : 1.5,
                          bgcolor: '#fef2f2',
                          borderRadius: 2,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: '#64748b', fontSize: isMobile ? '12px' : '14px' }}
                        >
                          Worst Score
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            color: '#ef4444',
                            fontSize: isMobile ? '12px' : '14px',
                          }}
                        >
                          {sidePanelContent.subtopicData.worst_score !== null
                            ? `${sidePanelContent.subtopicData.worst_score}%`
                            : 'N/A'}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          p: isMobile ? 1 : 1.5,
                          bgcolor: '#eff6ff',
                          borderRadius: 2,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: '#64748b', fontSize: isMobile ? '12px' : '14px' }}
                        >
                          Learning Status
                        </Typography>
                        <Chip
                          label={sidePanelContent.subtopicData.learning_status || 'Not Started'}
                          size="small"
                          sx={{ fontWeight: 600, fontSize: isMobile ? '10px' : '11px' }}
                          color={
                            sidePanelContent.subtopicData.learning_status === 'mastered'
                              ? 'success'
                              : sidePanelContent.subtopicData.learning_status === 'in-progress'
                                ? 'warning'
                                : 'default'
                          }
                        />
                      </Box>
                      {sidePanelContent.subtopicData.trend && (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            p: isMobile ? 1 : 1.5,
                            bgcolor: '#fefce8',
                            borderRadius: 2,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ color: '#64748b', fontSize: isMobile ? '12px' : '14px' }}
                          >
                            Trend
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {sidePanelContent.subtopicData.trend === 'improving' ? (
                              <TrendingUp sx={{ fontSize: isMobile ? 14 : 16, color: '#10b981' }} />
                            ) : sidePanelContent.subtopicData.trend === 'declining' ? (
                              <TrendingDown
                                sx={{ fontSize: isMobile ? 14 : 16, color: '#ef4444' }}
                              />
                            ) : null}
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                fontSize: isMobile ? '12px' : '14px',
                                color:
                                  sidePanelContent.subtopicData.trend === 'improving'
                                    ? '#10b981'
                                    : sidePanelContent.subtopicData.trend === 'declining'
                                      ? '#ef4444'
                                      : '#64748b',
                              }}
                            >
                              {sidePanelContent.subtopicData.trend}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </>
                )}

                {sidePanelContent.prerequisites?.length > 0 && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 700,
                        color: '#1e293b',
                        mb: 2,
                        fontSize: isMobile ? '13px' : '14px',
                      }}
                    >
                      ✅ Prerequisites
                    </Typography>
                    <List dense sx={{ bgcolor: '#f8fafc', borderRadius: 2, p: 1 }}>
                      {sidePanelContent.prerequisites.map((p, i) => (
                        <ListItem key={i} sx={{ py: 0.5 }}>
                          <Typography
                            variant="body2"
                            sx={{ color: '#475569', fontSize: isMobile ? '11px' : '13px' }}
                          >
                            • {p}
                          </Typography>
                        </ListItem>
                      ))}
                    </List>
                  </>
                )}
              </>
            )}

            {sidePanelContent.type === 'prerequisite' && (
              <>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: '#1e293b',
                    mb: 2,
                    fontSize: isMobile ? '16px' : '18px',
                  }}
                >
                  ✅ {sidePanelContent.prerequisite}
                </Typography>
                <Box sx={{ p: isMobile ? 1.5 : 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: '#64748b', lineHeight: 1.6, fontSize: isMobile ? '12px' : '14px' }}
                  >
                    This is a prerequisite concept required for understanding related subtopics.
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Chip
                      label={`Level ${sidePanelContent.level}`}
                      size="small"
                      sx={{ fontWeight: 600, fontSize: isMobile ? '10px' : '11px' }}
                      color="primary"
                    />
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </Paper>
      )}
    </>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '90vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', height: '90vh', bgcolor: '#ffffffff' }}>
      {/* Flow Area */}
      <Box
        ref={reactFlowWrapper}
        sx={{
          width: sidePanelContent && !isCompactView ? '70%' : '100%',
          position: 'relative',
          transition: 'width 0.3s ease',
        }}
      >
        {/* Tabs at the top of flow area */}
        <Box className="tabs-container-Aiboard"
          sx={{
            position: 'absolute',
            top: 10,
            left: 10,
            right: isMobile ? (sidePanelContent ? 140 : 70) : 10,
            zIndex: 5,
            bgcolor: '#ffffff',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            ...(isMobile && {
              left: 5,
              top: 5,
            }),
            p: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: isMobile ? 'calc(100% - 10px)' : 'calc(100% - 20px)',
          }}
        >
          <Tabs className='tabinwrapper'
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: isMobile ? '12px' : '14px',
                minHeight: isMobile ? '36px' : '42px',
                color: '#64748b',
                padding: isMobile ? '6px 12px' : '12px 16px',
                minWidth: isMobile ? '80px' : '120px',
              },
              '& .Mui-selected': {
                color: '#667eea',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#667eea',
                height: isMobile ? 2 : 3,
              },
              '& .MuiTabs-scrollButtons': {
                width: isMobile ? '32px' : '40px',
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
              },
            }}
          >
            {availableSubjects.map((subject, index) => (
              <Tab key={subject} label={subject} />
            ))}
          </Tabs>
          {/* Action Buttons - Always Visible */}
          <Box
            sx={{
              // position: 'absolute',
              top: isMobile ? 5 : 10,
              right: isMobile ? 5 : 10,
              zIndex: 5,
              display: 'flex',
              gap: 1,
              flexDirection: isMobile ? 'column' : 'row',
            }}
          >
            {/* Export Button */}
            {/* <Tooltip title={exporting ? 'Exporting...' : 'Export Performance Map'}>
              <IconButton
                onClick={exportToPdf}
                disabled={exporting}
                sx={{
                  bgcolor: '#ffffff',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  width: isMobile ? '36px' : '40px',
                  height: isMobile ? '36px' : '40px',
                  '&:hover': {
                    bgcolor: '#f8fafc',
                    borderColor: '#cbd5e1',
                  },
                  '&.Mui-disabled': {
                    bgcolor: '#f1f5f9',
                  },
                }}
              >
                {exporting ? (
                  <CircularProgress size={isMobile ? 16 : 20} />
                ) : (
                  <DownloadIcon fontSize={isMobile ? 'small' : 'medium'} sx={{ color: '#667eea' }} />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title="Export as Table PDF">
              <IconButton onClick={exportAsTablePdf} sx={{ bgcolor: '#ffffff', border: '1px solid #e2e8f0', }}>
                <TableViewIcon fontSize={isMobile ? 'small' : 'medium'} sx={{ color: '#667eea' }} />
              </IconButton>
            </Tooltip> */}
            <Tooltip title="Export as PDF">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleMenuOpen(e);
                }}
                sx={{ bgcolor: '#ffffff', border: '1px solid #e2e8f0' }}
              >
                <DownloadIcon fontSize={isMobile ? 'small' : 'medium'} sx={{ color: '#667eea' }} />
              </IconButton>
            </Tooltip>
            {/* Mobile Menu Button */}
            {isCompactView && sidePanelContent && (
              <IconButton
                onClick={() => setDrawerOpen(true)}
                sx={{
                  bgcolor: '#667eea',
                  color: 'white',
                  width: '36px',
                  height: '36px',
                  '&:hover': { bgcolor: '#5568d3' },
                  boxShadow: '0 2px 8px rgba(102, 126, 234, 0.4)',
                }}
              >
                <Menu fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>

        <div ref={reactFlowContainer} style={{ width: '100%', height: '100%' }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodeClick={handleNodeClick}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            fitView={false}
            style={{ background: '#f3f5e3ff' }}
            minZoom={0.01}
            maxZoom={8}
            attributionPosition="bottom-right"
            zoomOnScroll={true}
            zoomOnPinch={true}
            panOnScroll={true}
            panOnDrag={true}
            panOnScrollSpeed={1}
            preventScrolling={false}
          >
            <Controls
              showZoom={true}
              showFitView={true}
              showInteractive={false}
              style={{
                button: {
                  background: 'white',
                  borderColor: '#e2e8f0',
                  width: isMobile ? '32px' : '36px',
                  height: isMobile ? '32px' : '36px',
                },
              }}
              position="bottom-left"
            />
            <Background variant={BackgroundVariant.Dots} gap={16} size={1} color="#e2e8f0" />
          </ReactFlow>
        </div>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              minWidth: 150,
            },
          }}
        ><MenuItem
          onClick={() => {
            handleMenuClose();
            exportToPdf();
          }}
          sx={{ py: 1.5 }}
        >
            <ListItemIcon>
              <PictureAsPdfIcon fontSize="small" />
            </ListItemIcon>
            Export graph as pdf
          </MenuItem>

          <MenuItem
            onClick={() => {
              handleMenuClose();
              exportAsTablePdf();
            }}
            sx={{ py: 1.5 }}
          >            <ListItemIcon>
              <TableChartIcon fontSize="small" />
            </ListItemIcon>
            Export table as pdf
          </MenuItem>

        </Menu>
      </Box>

      {/* Desktop Side Panel */}
      {!isCompactView && sidePanelContent && (
        <Box
          sx={{
            width: '30%',
            overflowY: 'auto',
            p: 2,
            bgcolor: '#ffffff',
            borderLeft: '1px solid #e2e8f0',
          }}
        >
          <SidePanelContent />
        </Box>
      )}

      {/* Mobile Drawer */}
      {isCompactView && (
        <Drawer
          anchor="bottom"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{
            sx: {
              maxHeight: '80vh',
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
            },
          }}
        >
          <Box sx={{ p: 2, overflowY: 'auto' }}>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Details
              </Typography>
              <IconButton onClick={() => setDrawerOpen(false)} size="small">
                <Close />
              </IconButton>
            </Box>
            <SidePanelContent />
          </Box>
        </Drawer>
      )}
    </Box>
  );
};

export default function StudentPerformanceMap() {
  return (
    <ReactFlowProvider>
      <FlowComponent />
    </ReactFlowProvider>
  );
}
